/**
 * Locale registry — the single source of truth for which languages exist.
 *
 * PLANNED is the locked 9-locale target set (see the i18n rollout plan); a
 * locale only appears in the switcher once it has real catalogs on disk and is
 * listed in AVAILABLE. Keep the two lists apart deliberately: shipping a
 * half-translated locale silently falls back to English mid-page, which reads
 * as a bug to the customer rather than as work-in-progress.
 */

/** Tier 1 Latin · Tier 2 CJK · Tier 3 RTL — the locked target set. */
export const PLANNED = [
  { code: 'en',      label: 'English',    endonym: 'English',  short: 'EN', flag: '\u{1F1FA}\u{1F1F8}', tier: 0, dir: 'ltr' },
  { code: 'es',      label: 'Spanish',    endonym: 'Espa\u00f1ol',  short: 'ES', flag: '\u{1F1EA}\u{1F1F8}', tier: 1, dir: 'ltr' },
  { code: 'fr-FR',   label: 'French',     endonym: 'Fran\u00e7ais', short: 'FR', flag: '\u{1F1EB}\u{1F1F7}', tier: 1, dir: 'ltr' },
  { code: 'de',      label: 'German',     endonym: 'Deutsch',  short: 'DE', flag: '\u{1F1E9}\u{1F1EA}', tier: 1, dir: 'ltr' },
  { code: 'pt-BR',   label: 'Portuguese', endonym: 'Portugu\u00eas (Brasil)', short: 'PT', flag: '\u{1F1E7}\u{1F1F7}', tier: 1, dir: 'ltr' },
  { code: 'it',      label: 'Italian',    endonym: 'Italiano', short: 'IT', flag: '\u{1F1EE}\u{1F1F9}', tier: 1, dir: 'ltr' },
  { code: 'ja',      label: 'Japanese',   endonym: '\u65e5\u672c\u8a9e',    short: 'JA', flag: '\u{1F1EF}\u{1F1F5}', tier: 2, dir: 'ltr' },
  { code: 'ko',      label: 'Korean',     endonym: '\ud55c\uad6d\uc5b4',    short: 'KO', flag: '\u{1F1F0}\u{1F1F7}', tier: 2, dir: 'ltr' },

  // flag: null renders the code chip alone. Deliberate, not an oversight:
  // zh-Hant would require the Taiwan flag, which is commercially loaded for a
  // security vendor selling across APAC; Arabic spans 20+ countries, so any
  // single flag is wrong for most of the audience. Set a flag here only as an
  // explicit business decision.
  { code: 'zh-Hant', label: 'Chinese (Traditional)', endonym: '\u7e41\u9ad4\u4e2d\u6587', short: 'ZH', flag: null, tier: 2, dir: 'ltr' },
  { code: 'ar',      label: 'Arabic',     endonym: '\u0627\u0644\u0639\u0631\u0628\u064a\u0629',   short: 'AR', flag: null, tier: 3, dir: 'rtl' },
];

/**
 * Locales with catalogs actually committed under ./locales/<code>/.
 * A locale is listed here once its CORE namespaces are translated; namespaces it
 * has not reached yet fall back to English per key, which is why the parity test
 * enforces completeness per namespace-that-exists rather than across the board.
 */
export const AVAILABLE = ['en', 'es', 'fr-FR', 'de', 'pt-BR', 'it', 'ja', 'ko', 'zh-Hant', 'ar'];

export const DEFAULT_LOCALE = 'en';

export const SUPPORTED_CODES = PLANNED.map((l) => l.code);

const BY_CODE = new Map(PLANNED.map((l) => [l.code, l]));

export const getLocale = (code) => BY_CODE.get(code) || BY_CODE.get(DEFAULT_LOCALE);

export const isRtl = (code) => getLocale(code).dir === 'rtl';

/** Locales offered in the switcher, in PLANNED order. */
export const availableLocales = () => PLANNED.filter((l) => AVAILABLE.includes(l.code));
